/**
 * ProvisionedKeysSection — admin view of org-minted OpenRouter keys
 * (ADR-0014 governance): who has one, today's spend against the daily limit,
 * and a disable/re-enable toggle (offboarding, runaway spend). Rendered only
 * for cloud admins; the backend 403s everyone else regardless.
 */

import { useEffect, useState } from "react";
import { apiFetch } from "@/api/client";
import { useToastStore } from "@/components/common/Toast";

interface ProvisionedKey {
  user_id: string;
  email: string;
  name: string;
  key_hash: string;
  usage_daily: number | null;
  limit: number | null;
  limit_remaining: number | null;
  disabled: boolean | null;
  live: boolean;
}

interface KeysResponse {
  keys: ProvisionedKey[];
  limit_usd: number;
  limit_reset: string;
}

export function ProvisionedKeysSection() {
  const [data, setData] = useState<KeysResponse | null>(null);
  const [error, setError] = useState(false);
  const [busyHash, setBusyHash] = useState<string | null>(null);
  const addToast = useToastStore((s) => s.add);

  const load = async () => {
    try {
      setData(await apiFetch<KeysResponse>("/provision/keys"));
      setError(false);
    } catch {
      setError(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (k: ProvisionedKey) => {
    if (busyHash) return;
    setBusyHash(k.key_hash);
    try {
      await apiFetch(`/provision/keys/${encodeURIComponent(k.key_hash)}/toggle`, {
        method: "POST",
        body: JSON.stringify({ disabled: !k.disabled }),
      });
      addToast(
        k.disabled ? `Key re-enabled for ${k.email}.` : `Key disabled for ${k.email}.`,
        "success",
      );
      await load();
    } catch (e) {
      addToast((e as Error).message || "Couldn't update the key.", "error");
    } finally {
      setBusyHash(null);
    }
  };

  if (error || data === null) return null; // non-admin (403) or still loading

  return (
    <div className="mt-8">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-bold text-space">Provisioned OpenRouter keys</h3>
        <span className="text-xs text-space-40">
          ${data.limit_usd}/{data.limit_reset === "daily" ? "day" : data.limit_reset} per member
        </span>
      </div>
      {data.keys.length === 0 ? (
        <p className="text-xs text-space-40">
          No org-minted keys yet — members get one automatically at their next sign-in.
        </p>
      ) : (
        <div className="rounded-xl border border-space-30 divide-y divide-space-30/50">
          {data.keys.map((k) => (
            <div key={k.key_hash} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-space truncate">
                  {k.name || k.email}
                  {k.disabled && (
                    <span className="ml-2 inline-block px-1.5 py-px rounded-full bg-hydra-50 text-hydra text-[10px] font-semibold">
                      Disabled
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-space-40 truncate">
                  {k.email}
                  {k.live && k.usage_daily != null
                    ? ` · $${k.usage_daily.toFixed(2)} today${k.limit != null ? ` of $${k.limit}` : ""}`
                    : " · usage unavailable"}
                </p>
              </div>
              <button
                onClick={() => toggle(k)}
                disabled={busyHash === k.key_hash}
                className={`shrink-0 py-1.5 px-3 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${
                  k.disabled
                    ? "text-jove hover:bg-jove-10/50"
                    : "text-hydra hover:bg-hydra-50/30"
                }`}
              >
                {busyHash === k.key_hash ? "…" : k.disabled ? "Re-enable" : "Disable"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
